"use client";

import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useTranslation } from "react-i18next";
import { Icon } from "@iconify-icon/react";
import { motion } from "motion/react";

import { toast } from "@/utils/customToast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  ChildSubject,
  getCurrencySymbol,
  PriceCurrency,
  type PricingConfiguration,
  renderPriceTiers,
  SubjectWithCurriculumLevels,
} from "@/features/calendar/types/pricing";
import { createSubjectPricing } from "@/features/calendar/api/Pricing";

const subjectPricingSchema = (t: any) =>
  z.object({
    amount: z.number().min(0.01, t("forms:templates.validation.priceMinimium")),
    currency: z.nativeEnum(PriceCurrency),
  });

type SubjectPricingFormValues = z.infer<ReturnType<typeof subjectPricingSchema>>;

interface PricingFormProps {
  tutorProfileSubjectId: string;
  subjectId: string; // Actual subject ID
  subjectsData?: SubjectWithCurriculumLevels[];
  onCancel: () => void;

  // For single pricing mode
  curriculumLevelId?: string;
  existingPricing?: PricingConfiguration | null;
  onSave?: (savedPricing: PricingConfiguration) => void;

  // For "Apply to All" / Batch mode
  applyToLevels?: Array<{ id: string; name: string }>;
  allPricingsForSubjectForBatch?: PricingConfiguration[];
  onBatchOperationComplete?: (newAndUpdatedPricings: PricingConfiguration[]) => void;
}

export const PricingForm = ({
  tutorProfileSubjectId,
  subjectsData,
  onCancel,
  curriculumLevelId,
  existingPricing,
  onSave,
  applyToLevels,
  allPricingsForSubjectForBatch,
  onBatchOperationComplete,
}: PricingFormProps) => {
  const { t } = useTranslation(["common", "calendar", "forms"]);

  const form = useForm<SubjectPricingFormValues>({
    resolver: zodResolver(subjectPricingSchema(t)),
    defaultValues: {
      amount: existingPricing?.amount || undefined,
      currency: (existingPricing?.currency as PriceCurrency) || PriceCurrency.Usd,
    },
  });

  const currency = form.watch("currency");

  const onSubmit = async (values: SubjectPricingFormValues) => {
    const { amount, currency: formCurrency } = values;

    // Find subject data - search both parent subjects and children
    let subjectWithLevelsData: SubjectWithCurriculumLevels | ChildSubject | null = null;

    // First check if it's a parent subject
    const parentSubject = subjectsData?.find((s) => s.id === tutorProfileSubjectId);

    if (parentSubject) {
      subjectWithLevelsData = parentSubject;
    } else if (subjectsData) {
      // If not found at top level, search in children
      for (const parent of subjectsData) {
        if (parent.children) {
          const child = parent.children.find((c) => c.id === tutorProfileSubjectId);

          if (child) {
            subjectWithLevelsData = child;
            break;
          }
        }
      }
    }

    // Handle both parent subjects (direct properties) and child subjects (nested in subject property)
    let subjectInfoForDisplay = null;

    if (subjectWithLevelsData) {
      if ("subject" in subjectWithLevelsData && subjectWithLevelsData.subject) {
        // Child subject structure
        subjectInfoForDisplay = {
          id: subjectWithLevelsData.subject.id,
          name: subjectWithLevelsData.subject.name,
          tutor_profile_subject_id: tutorProfileSubjectId,
        };
      } else if ("name" in subjectWithLevelsData) {
        // Parent subject structure
        subjectInfoForDisplay = {
          id: subjectWithLevelsData.id,
          name: subjectWithLevelsData.name,
          tutor_profile_subject_id: tutorProfileSubjectId,
        };
      }
    }

    // Batch "Apply to All" Mode
    if (
      applyToLevels &&
      applyToLevels.length > 0 &&
      allPricingsForSubjectForBatch &&
      onBatchOperationComplete
    ) {
      let successCount = 0;
      const results: PricingConfiguration[] = [];
      let batchHasErrors = false;

      for (const level of applyToLevels) {
        try {
          const response = await createSubjectPricing({
            tutor_profile_subject_id: tutorProfileSubjectId,
            curriculum_level_id: level.id,
            amount,
            currency: formCurrency,
          });
          const savedPricingDataFromApi = response.data.data;

          const completeSavedPricing: PricingConfiguration = {
            ...savedPricingDataFromApi,
            amount,
            currency: formCurrency,
            subject: subjectInfoForDisplay ?? undefined,
            curriculum_level: { id: level.id, name: level.name },
            is_subject_specific: true,
          };

          results.push(completeSavedPricing);
          successCount++;
        } catch (error: any) {
          console.error(`Failed to save pricing for level ${level.name}:`, error);
          batchHasErrors = true;
          // No per-item toast here, summary toast after loop
        }
      }

      if (!batchHasErrors && successCount === applyToLevels.length && applyToLevels.length > 0) {
        toast.success(t("calendar:pricing.subjectPricingUpdated")); // Generic success for batch
      } else if (batchHasErrors && applyToLevels.length > 0) {
        toast.error(t("common:errors.failedToSavePricing")); // Generic error if any failure in batch
      }
      // If applyToLevels was empty, no toasts.

      onBatchOperationComplete(results); // results might be partial if errors occurred

      return;
    }

    // Single Pricing Mode
    if (onSave && curriculumLevelId) {
      try {
        const levelInfoForDisplay =
          curriculumLevelId && subjectWithLevelsData && subjectWithLevelsData.curriculum_levels
            ? subjectWithLevelsData.curriculum_levels.find((l) => l.id === curriculumLevelId)
            : null;

        const response = await createSubjectPricing({
          tutor_profile_subject_id: tutorProfileSubjectId,
          curriculum_level_id: curriculumLevelId,
          amount,
          currency: formCurrency,
        });
        const savedPricingDataFromApi = response.data.data;

        toast.success(
          existingPricing
            ? t("calendar:pricing.subjectPricingUpdated")
            : t("calendar:pricing.subjectPricingCreated"),
        );

        const displayPricing: PricingConfiguration = {
          ...savedPricingDataFromApi,
          amount,
          currency: formCurrency,
          subject: subjectInfoForDisplay ?? undefined,
          curriculum_level: levelInfoForDisplay
            ? { id: levelInfoForDisplay.id, name: levelInfoForDisplay.name }
            : undefined,
        };

        onSave(displayPricing);
      } catch (error: any) {
        console.error("Failed to save subject pricing (single mode):", error);
        const errorDetail = error?.response?.data?.detail;
        const minPrice = error?.response?.data?.min_price;

        if (errorDetail === "price_too_small" && minPrice !== undefined) {
          form.setError("amount", {
            type: "manual",
            message: t("calendar:pricing.minimumPriceError", {
              symbol: getCurrencySymbol(formCurrency as PriceCurrency),
              price: minPrice,
            }),
          });
        } else if (error?.response?.data?.errors?.amount?.[0]) {
          form.setError("amount", {
            type: "manual",
            message: error.response.data.errors.amount[0],
          });
        } else {
          toast.error(t("common:errors.failedToSavePricing"));
        }
      }
    } else if (onSave && !curriculumLevelId) {
      toast.error(t("common:errors.failedToSavePricing"));
    }
  };

  return (
    <Form {...form}>
      <motion.form
        animate={{ opacity: 1 }}
        className="p-1"
        initial={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        onSubmit={form.handleSubmit(onSubmit)}
      >
        <div className="space-y-4">
          <FormField
            control={form.control}
            name="currency"
            render={({ field }) => (
              <FormItem>
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-0">
                  <FormLabel className="text-accent-foreground text-xs font-medium sm:text-sm">
                    {t("calendar:pricing.pricePerLesson")}
                  </FormLabel>
                  <div className="bg-muted hover:bg-side-accent flex h-7 items-center self-start rounded-full px-2 sm:h-8 sm:self-auto sm:px-3">
                    <FormControl>
                      <Select
                        value={field.value}
                        onValueChange={(value) => {
                          field.onChange(value);
                          form.clearErrors("amount");
                        }}
                      >
                        <SelectTrigger className="text-accent-foreground h-7 min-w-[64px] border-0 bg-transparent p-0 px-1.5 text-xs shadow-none focus:ring-0 focus:ring-offset-0 sm:h-8 sm:min-w-[70px] sm:px-2 sm:text-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent align={"center"} className={"z-[60]"}>
                          <SelectItem value={PriceCurrency.Eur}>
                            <div className="flex items-center gap-2">
                              <span className="text-xs">€</span>EUR
                            </div>
                          </SelectItem>
                          <SelectItem value={PriceCurrency.Gbp}>
                            <div className="flex items-center gap-2">
                              <span className="text-xs">£</span>GBP
                            </div>
                          </SelectItem>
                          <SelectItem value={PriceCurrency.Pln}>
                            <div className="flex items-center gap-2">
                              <span className="text-xs">zł</span>PLN
                            </div>
                          </SelectItem>
                          <SelectItem value={PriceCurrency.Usd}>
                            <div className="flex items-center gap-2">
                              <span className="text-xs">$</span>USD
                            </div>
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </FormControl>
                  </div>
                </div>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="amount"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <div className="group relative">
                    <div className="absolute top-0 left-0 flex h-9 w-9 items-center justify-center rounded-l-md bg-transparent sm:top-1 sm:h-10 sm:w-14">
                      <span className="text-accent-foreground text-base font-medium sm:text-lg">
                        {getCurrencySymbol(currency)}
                      </span>
                    </div>
                    <Input
                      {...field}
                      className={`bg-muted hover:bg-side-accent focus:bg-side-accent group-hover:bg-side-accent h-9 appearance-none border-0 pl-9 text-base font-medium transition-colors sm:h-12 sm:pl-14 sm:text-lg [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none ${form.formState.errors.amount ? "border border-red-500" : ""}`}
                      min="0.01"
                      placeholder="0.00"
                      step="0.01"
                      type="number"
                      value={field.value || ""}
                      onChange={(e) => {
                        const value =
                          e.target.value === "" ? undefined : Number.parseFloat(e.target.value);

                        if (value !== undefined && (value < 0 || value > 9999.99)) return;
                        field.onChange(value);
                        form.clearErrors("amount");
                      }}
                    />
                  </div>
                </FormControl>
                <FormMessage className="text-destructive flex items-center gap-1 pt-1 text-xs">
                  {form.formState.errors.amount && (
                    <Icon
                      className="flex-shrink-0"
                      height="14"
                      icon="mdi:alert-circle-outline"
                      width="14"
                    />
                  )}
                  <span>{form.formState.errors.amount?.message}</span>
                </FormMessage>
              </FormItem>
            )}
          />

          <div>
            <h4 className="text-muted-foreground mb-2 text-[11px] font-medium sm:text-xs">
              {t("calendar:pricing.suggestedPrices")}
            </h4>
            <div className="grid grid-cols-3 gap-2 min-[380px]:grid-cols-4 sm:grid-cols-5">
              {renderPriceTiers(currency, (price) => {
                form.setValue("amount", price);
                form.clearErrors("amount");
              })}
            </div>
          </div>
        </div>

        <div className="mt-5 flex items-center gap-2 sm:mt-6">
          <Button
            className="h-9 flex-1 sm:h-10"
            size="sm"
            type="button"
            variant="outline"
            onClick={onCancel}
          >
            <p className={"flex items-center gap-1"}>
              <Icon height="1.25rem" icon="mdi:cancel-bold" width="1.25rem" />
              {t("common:cancel")}
            </p>
          </Button>
          <Button
            className="h-9 flex-1 border sm:h-10"
            disabled={
              form.formState.isSubmitting || !form.watch("amount") || !!form.formState.errors.amount
            }
            size="sm"
            type="submit"
            variant="foreground"
          >
            {form.formState.isSubmitting ? (
              <>
                <Icon className="mr-1 animate-spin" height="14" icon="mdi:loading" width="14" />
                {t("common:saving")}
              </>
            ) : (
              <p className={"flex items-center gap-1"}>
                <Icon height="1.25rem" icon="mdi:content-save-outline" width="1.25rem" />
                {t("common:save")}
              </p>
            )}
          </Button>
        </div>
      </motion.form>
    </Form>
  );
};
